import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './hooks/useAuth';
import { BackgroundJobsProvider } from './hooks/useBackgroundJobs';
import { ProtectedRoute } from './components/auth';
import { AppLayout } from './components/layout';
import RouteTracker from './components/RouteTracker';
import {
  LoginPage,
  RegisterPage,
  HomePage,
  BrowsePage,
  BooksPage,
  DraftsPage,
  NewBookPage,
  EditBookPage,
  BookPage,
  BookSyncPage,
  CoverEditorPage,
  TitlePageEditorPage,
  CopyrightEditorPage,
  DedicationEditorPage,
  EpigraphEditorPage,
  AdminPage,
  AiPromptLogsPage,
  AiEvaluationsPage,
  ErrorLogsPage,
  ComponentsLibraryPage,
  ProfilePage,
  ChapterFlowSelectionPage,
  RefinementWorkspacePage,
  PlanManagerPage,
  SceneFlowSelectionPage,
  SceneEditorPage,
  ChapterFocusPage,
  TranslationReaderPage,
  TranslationSettingsPage,
  BookReaderPage,
} from './pages';

import './lib/i18n';
import './styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BackgroundJobsProvider>
          <BrowserRouter>
            <RouteTracker />
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />

              {/* Public route with layout */}
              <Route path="/" element={<AppLayout />}>
                <Route index element={<HomePage />} />
                <Route path="browse" element={<BrowsePage />} />
              </Route>

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="books" element={<BooksPage />} />
                <Route path="books/drafts" element={<DraftsPage />} />
                <Route path="books/new" element={<NewBookPage />} />
                <Route path="books/new/:bookId/basics" element={<NewBookPage />} />
                <Route path="books/new/:bookId/description" element={<NewBookPage />} />
                <Route path="books/new/:bookId/storyteller" element={<NewBookPage />} />
                <Route path="books/:bookId/edit" element={<EditBookPage />} />
                <Route path="books/:bookId/edit/audience" element={<EditBookPage />} />
                <Route path="books/:bookId/edit/description" element={<EditBookPage />} />
                <Route path="books/:bookId/edit/cover" element={<EditBookPage />} />
                <Route path="books/:bookId/edit/storyteller" element={<EditBookPage />} />
                <Route path="books/:bookId/cover-editor" element={<CoverEditorPage />} />
                <Route path="books/:bookId/front-matter/title" element={<TitlePageEditorPage />} />
                <Route
                  path="books/:bookId/front-matter/copyright"
                  element={<CopyrightEditorPage />}
                />
                <Route
                  path="books/:bookId/front-matter/dedication"
                  element={<DedicationEditorPage />}
                />
                <Route
                  path="books/:bookId/front-matter/epigraph"
                  element={<EpigraphEditorPage />}
                />
                <Route
                  path="books/:bookId/chapters-first/select"
                  element={<ChapterFlowSelectionPage />}
                />
                <Route path="books/:bookId/refine" element={<RefinementWorkspacePage />} />
                <Route path="books/:bookId/plan" element={<PlanManagerPage />} />
                <Route path="books/:bookId/read" element={<BookReaderPage />} />
                <Route path="books/:bookId/sync" element={<BookSyncPage />} />
                <Route
                  path="translate"
                  element={<Navigate to="/books?tab=translations" replace />}
                />
                <Route path="translate/:projectId" element={<TranslationReaderPage />} />
                <Route path="translate/:projectId/settings" element={<TranslationSettingsPage />} />
                <Route
                  path="books/:bookId/chapters/:chapterId/scenes-first/select"
                  element={<SceneFlowSelectionPage />}
                />
                <Route path="books/:bookId/chapters/:chapterId" element={<ChapterFocusPage />} />
                <Route path="books/:bookId/scenes/:sceneId" element={<SceneEditorPage />} />
                <Route
                  path="books/:bookId/scenes/:sceneId/edit"
                  element={<Navigate to=".." relative="path" replace />}
                />
                <Route path="books/:bookId" element={<BookPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="admin" element={<AdminPage />} />
                <Route path="admin/ai-prompt-logs" element={<AiPromptLogsPage />} />
                <Route path="admin/ai-evaluations" element={<AiEvaluationsPage />} />
                <Route path="admin/error-logs" element={<ErrorLogsPage />} />
                <Route path="admin/components" element={<ComponentsLibraryPage />} />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </BackgroundJobsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
